import { Context, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CustomerLocationCtx } from 'src/context/FacilityContext'
import { DEVIATION_NOT_FOUND } from 'src/constant'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useGetDeviation, useGetDeviationAdmin } from '../../../api/deviation'
import { CenteredSpinner } from '../../../components/Spinner'
import UpdateDeviationAdminDialog from '../../../dialogs/deviation/UpdateDeviationAdminDialog'
import UpdateDeviationDialog from '../../../dialogs/deviation/UpdateDeviationDialog'
import { IDeviation } from '../../../generated-types/deviation'
import { CenteredBox } from '../documents/DocumentContainer'
import { CreateDeviationFollowUpDialog, CreateDeviationFollowUpDialogAdmin } from './CreateDeviationFollowUpDialog'
import { GenericDeviationContainer } from './GenericDeviationContainer'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { permissionValue } from 'src/enums/permissionValue'
import { AuthorizationType } from 'src/enums/auth'

export const DeviationContainer: React.FC<{ isAdminLayout?: boolean; context?: Context<any> | null }> = ({
  isAdminLayout,
  context,
}) => {
  const { customerId: clinetID, id, deviationId, localityId } = useParams()
  const customerId = clinetID ?? id
  const { data, isLoading, error } = useGetDeviation(customerId as EntityId, deviationId as EntityId)
  const { showDialog } = useDialogCtx()
  const permission = useContext(context ?? CustomerLocationCtx)?.permission

  if (isLoading) return <CenteredSpinner />
  if (error && error['data'].message == DEVIATION_NOT_FOUND)
    return (
      <CenteredBox className="flex items-center justify-center min-h-screen" minWidth="100%">
        <EmptyStatusCard imageWidth="200px" content="deviation-data-unavailable-description" />
      </CenteredBox>
    )

  const onAdd = async (defaultStatus?: boolean) => {
    const added = await showDialog(
      CreateDeviationFollowUpDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          deviationId: deviationId as EntityId,
          dueDate: data?.dueDate,
          priority: data?.priority,
          localityId: localityId as EntityId,
          positionId: data?.positionId as EntityId,
          validationRequired: data?.validationRequired,
          defaultStatus,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const updateDeviation = async (deviation: IDeviation) => {
    const updated = await showDialog(
      UpdateDeviationDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          deviation: deviation,
          permission,
        },
      },
      undefined,
      true,
    )
    if (!updated) return
  }
  return (
    <AuthorizationProvider
      Context={context}
      permissions={
        !isAdminLayout
          ? [
              permissionValue.Locality.Deviation.Read,
              permissionValue.Locality.Mooring.Deviation.Read,
              permissionValue.Locality.Mooring.Delete,
              permissionValue.Locality.Mooring.Write,
              permissionValue.Locality.Position.Write,
              permissionValue.ServiceStation.Read,
              permissionValue.Storage.Read,
              permissionValue.Locality.MainComponent.Deviation.Read,
              permissionValue.Locality.Task.Read,
              permissionValue.Locality.Service.Read,
              permissionValue.ClientAdminPermission
            ]
          : [permissionValue.SystemAdminPermission]
      }
      type={AuthorizationType.Page}
    >
      <GenericDeviationContainer
        data={data}
        customerId={customerId}
        deviationId={deviationId as EntityId}
        onAdd={onAdd}
        updateDeviation={updateDeviation}
        isAdminLayout={isAdminLayout}
        localityId={localityId as EntityId}
        context={context}
      />
    </AuthorizationProvider>
  )
}

export const DeviationContainerAdmin: React.FC = () => {
  const { deviationId, localityId } = useParams()
  const { data, isLoading } = useGetDeviationAdmin(deviationId as EntityId)
  const { showDialog } = useDialogCtx()

  if (isLoading) return <CenteredSpinner />

  const onAdd = async (defaultStatus?: boolean) => {
    const added = await showDialog(
      CreateDeviationFollowUpDialogAdmin,
      {
        componentProps: {
          deviationId: deviationId as EntityId,
          dueDate: data?.dueDate,
          priority: data?.priority,
          defaultStatus,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const updateDeviation = async (deviation: IDeviation) => {
    const updated = await showDialog(
      UpdateDeviationAdminDialog,
      {
        componentProps: {
          deviation: deviation,
        },
      },
      undefined,
      true,
    )
    if (!updated) return
  }
  return (
    <GenericDeviationContainer
      data={data}
      deviationId={deviationId as EntityId}
      onAdd={onAdd}
      updateDeviation={updateDeviation}
      isAdminLayout={true}
      localityId={localityId as EntityId}
    />
  )
}

export default DeviationContainer
